import { domainEventSchema, type DomainEvent, type EventRejection, type EventType } from '@pms/shared';

import { db, getDeviceId } from './db.js';
import type { OutboxRecord } from './types.js';

/**
 * Исходящая очередь.
 *
 * Порядок работы жёсткий: сначала запись в Dexie, потом показ результата
 * оператору. Интерфейс никогда не ждёт сервер — в цеху сети может не быть
 * часами, и сдача смены от этого зависеть не должна.
 */

export interface EnqueueInput {
  type: EventType;
  aggregateType: string;
  aggregateId: string;
  userId: string;
  payload: Record<string, unknown>;
  correctsEventId?: string;
  correctionReason?: string;
}

export async function enqueue(input: EnqueueInput): Promise<OutboxRecord> {
  const deviceId = await getDeviceId();
  const event: DomainEvent = domainEventSchema.parse({
    id: crypto.randomUUID(),
    type: input.type,
    aggregateType: input.aggregateType,
    aggregateId: input.aggregateId,
    userId: input.userId,
    deviceId,
    payload: input.payload,
    schemaVersion: 1,
    // Время устройства в момент нажатия. Сервер его не подменяет.
    occurredAt: new Date().toISOString(),
    ...(input.correctsEventId ? { correctsEventId: input.correctsEventId } : {}),
    ...(input.correctionReason ? { correctionReason: input.correctionReason } : {}),
  });

  const record: OutboxRecord = {
    id: event.id,
    status: 'pending',
    event,
    createdAt: new Date().toISOString(),
    attempts: 0,
  };
  await db.outbox.add(record);
  notifyOutboxChanged();
  return record;
}

/** Событие «в очереди появилась запись». Движок обмена слушает его и пробует
 *  отправить сразу, не дожидаясь таймера. Через window, чтобы не было
 *  кольцевой зависимости между очередью и движком. */
export const OUTBOX_CHANGED_EVENT = 'pms:outbox-changed';

export function notifyOutboxChanged(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(OUTBOX_CHANGED_EVENT));
  }
}

/**
 * Забрать пачку на отправку, пометив её как отправляемую.
 * Помечаем внутри транзакции, чтобы два параллельных прохода воркера
 * не отправили одно и то же дважды.
 */
export async function claimBatch(limit: number): Promise<OutboxRecord[]> {
  return db.transaction('rw', db.outbox, async () => {
    // Порядок отправки — по времени создания: корректировка не должна уйти
    // раньше события, которое она исправляет.
    const pending = await db.outbox.where('status').equals('pending').sortBy('createdAt');
    const batch = pending.slice(0, limit);
    if (batch.length === 0) return [];
    await db.outbox.bulkPut(batch.map((record) => ({ ...record, status: 'sending' as const })));
    return batch;
  });
}

/** Вернуть пачку в очередь после сетевой ошибки. Запись остаётся, ничего не теряется. */
export async function releaseBatch(ids: string[], error: string): Promise<void> {
  await db.transaction('rw', db.outbox, async () => {
    const records = await db.outbox.bulkGet(ids);
    const updated = records
      .filter((record): record is OutboxRecord => Boolean(record))
      .map((record) => ({
        ...record,
        status: 'pending' as const,
        attempts: record.attempts + 1,
        lastError: error,
      }));
    await db.outbox.bulkPut(updated);
  });
}

/** Применить ответ сервера: что принято, что отклонено по существу. */
export async function applyPushResult(
  accepted: string[],
  rejected: EventRejection[],
): Promise<void> {
  const now = new Date().toISOString();
  await db.transaction('rw', db.outbox, async () => {
    if (accepted.length > 0) {
      const records = await db.outbox.bulkGet(accepted);
      await db.outbox.bulkPut(
        records
          .filter((record): record is OutboxRecord => Boolean(record))
          .map((record) => ({ ...record, status: 'synced' as const, syncedAt: now, lastError: undefined })),
      );
    }
    for (const rejection of rejected) {
      const record = await db.outbox.get(rejection.id);
      if (!record) continue;
      await db.outbox.put({
        ...record,
        status: 'rejected',
        rejectedAt: now,
        rejectionReason: rejection.reason,
      });
    }
  });
}

/**
 * Сервер отверг пачку целиком (400 на самом запросе). Повторять бессмысленно —
 * помечаем записи отклонёнными, иначе очередь встанет навсегда.
 */
export async function rejectBatch(ids: string[], reason: string): Promise<void> {
  const now = new Date().toISOString();
  await db.transaction('rw', db.outbox, async () => {
    const records = await db.outbox.bulkGet(ids);
    await db.outbox.bulkPut(
      records
        .filter((record): record is OutboxRecord => Boolean(record))
        .map((record) => ({
          ...record,
          status: 'rejected' as const,
          rejectedAt: now,
          rejectionReason: reason,
        })),
    );
  });
}

/**
 * После перезагрузки страницы записи могли остаться в состоянии «отправляется».
 * Возвращаем их в очередь: повторная отправка безопасна, id тот же.
 */
export async function recoverStuckSending(): Promise<number> {
  return db.transaction('rw', db.outbox, async () => {
    const stuck = await db.outbox.where('status').equals('sending').toArray();
    if (stuck.length === 0) return 0;
    await db.outbox.bulkPut(stuck.map((record) => ({ ...record, status: 'pending' as const })));
    return stuck.length;
  });
}

/** Повторить отправку отклонённой или зависшей записи вручную. */
export async function retryRecord(id: string): Promise<void> {
  const record = await db.outbox.get(id);
  if (!record || record.status === 'synced') return;
  await db.outbox.put({
    ...record,
    status: 'pending',
    attempts: 0,
    lastError: undefined,
    rejectedAt: undefined,
    rejectionReason: undefined,
  });
  notifyOutboxChanged();
}

export async function retryAllRejected(): Promise<number> {
  const rejected = await db.outbox.where('status').equals('rejected').toArray();
  await db.outbox.bulkPut(
    rejected.map((record) => ({
      ...record,
      status: 'pending' as const,
      attempts: 0,
      rejectedAt: undefined,
      rejectionReason: undefined,
    })),
  );
  if (rejected.length > 0) notifyOutboxChanged();
  return rejected.length;
}

export interface OutboxCounts {
  pending: number;
  sending: number;
  rejected: number;
  synced: number;
}

export async function countByStatus(): Promise<OutboxCounts> {
  const [pending, sending, rejected, synced] = await Promise.all([
    db.outbox.where('status').equals('pending').count(),
    db.outbox.where('status').equals('sending').count(),
    db.outbox.where('status').equals('rejected').count(),
    db.outbox.where('status').equals('synced').count(),
  ]);
  return { pending, sending, rejected, synced };
}

/** Сколько записей ждут отправки — это число видит оператор в шапке. */
export async function countUnsent(): Promise<number> {
  const counts = await countByStatus();
  return counts.pending + counts.sending;
}

/**
 * Подтверждённые события храним неделю — чтобы оператор мог убедиться,
 * что отчёт ушёл. Дальше чистим: место на дешёвом телефоне не бесконечное.
 */
export async function pruneSynced(maxAgeMs = 7 * 24 * 60 * 60 * 1000): Promise<number> {
  const threshold = Date.now() - maxAgeMs;
  const stale = await db.outbox
    .where('status')
    .equals('synced')
    .filter((record) => {
      const syncedAt = record.syncedAt ? Date.parse(record.syncedAt) : 0;
      return syncedAt > 0 && syncedAt < threshold;
    })
    .primaryKeys();
  if (stale.length > 0) await db.outbox.bulkDelete(stale);
  return stale.length;
}

export async function listRecent(limit = 100): Promise<OutboxRecord[]> {
  const all = await db.outbox.orderBy('createdAt').reverse().limit(limit).toArray();
  return all;
}
